import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import appConfig from '@/config/appConfig';
import categories from '@/config/categories';
import iconMap from '@/config/iconMap';

const Landing = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [activeCategory, setActiveCategory] = useState('all');
  const [appSettings, setAppSettings] = useState({});
  const { signup, login, user, forgotPassword } = useAuth();
  const navigate = useNavigate();

  // Public read (RLS allows everyone) so the logged-out grid shows the same
  // free / premium state an admin has set, matching the Dashboard.
  useEffect(() => {
    supabase.from('app_settings').select('app_id, is_premium').then(({ data, error }) => {
      if (!error && data) {
        setAppSettings(Object.fromEntries(data.map((row) => [row.app_id, row.is_premium])));
      }
    });
  }, []);

  if (user) {
    navigate('/dashboard');
    return null;
  }

  const isPremium = (app) => (appSettings[app.id] !== undefined ? appSettings[app.id] : app.isPremium);
  const visibleApps = appConfig
    .filter((app) => !app.inDevelopment)
    .filter((app) => activeCategory === 'all' || app.category === activeCategory);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = isLogin 
      ? await login(formData.email, formData.password)
      : await signup(formData.email, formData.password, formData.name);
    
    if (success) navigate('/dashboard');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsSendingReset(true);
    try {
      const ok = await forgotPassword(forgotEmail);
      if (ok) {
        toast.success('If an account with that email exists, a reset link has been sent.');
        setShowForgotPassword(false);
        setForgotEmail('');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] via-[#E0F7FA] to-[#FFF4E6] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#81B29A] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#F2CC8F] rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <section className="text-center mb-20 mt-12">
          <h2 className="text-5xl md:text-7xl font-bold text-[#2D3748] mb-6 tracking-tight leading-tight">
            Sensory Regulation
            <br />
            <span className="text-[#81B29A]">Made Simple</span>
          </h2>
          <p className="text-xl md:text-2xl text-[#4A5568] max-w-3xl mx-auto leading-relaxed mb-12">
            A therapeutic toolkit designed for neurodivergent individuals, educators, therapists and anyone who needs a moment of calm or specific sensory input in our busy world.
          </p>
          
          <button
            data-testid="start-exploring-btn"
            onClick={() => setShowAuth(true)}
            className="bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-12 py-5 rounded-full font-bold text-lg hover:-translate-y-1 transition-all duration-300 shadow-xl hover:shadow-2xl"
          >
            Start Exploring
          </button>
        </section>

        <section className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12">
          {categories.map(({ icon: Icon, label, color, key }) => {
            const active = activeCategory === key;
            return (
              <button
                key={label}
                onClick={() => setActiveCategory(active ? 'all' : key)}
                className={`bg-white rounded-3xl p-8 text-center shadow-sm hover:shadow-md transition-all duration-300 border card-hover focus:outline-none focus:ring-2 focus:ring-[#81B29A]/40 ${
                  active ? 'border-[#81B29A] ring-2 ring-[#81B29A]/40' : 'border-slate-100'
                }`}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: color }}>
                  <Icon className="w-8 h-8 text-[#2D3748]" />
                </div>
                <h3 className="text-xl font-bold text-[#2D3748]">{label}</h3>
              </button>
            );
          })}
        </section>

        {/* App grid — visible to logged-out visitors so they can see the
            toolkit, but every app requires (at least) a free account, so each
            button prompts sign-in. */}
        <section className="mb-20">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h3 className="text-3xl font-bold text-[#2D3748]">
              {activeCategory === 'all'
                ? 'Explore the toolkit'
                : `${categories.find((c) => c.key === activeCategory)?.label} tools`}
            </h3>
            {activeCategory !== 'all' && (
              <button
                onClick={() => setActiveCategory('all')}
                className="text-[#81B29A] font-semibold hover:underline text-sm"
              >
                Show all
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleApps.map((app) => {
              const Icon = iconMap[app.icon];
              const premium = isPremium(app);
              return (
                <div
                  key={app.id}
                  className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 relative overflow-hidden flex flex-col"
                >
                  <div
                    className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-20"
                    style={{ backgroundColor: app.color }}
                  ></div>
                  <div className="relative z-10 flex flex-col flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: app.color }}
                      >
                        {Icon && <Icon className="w-7 h-7 text-[#2D3748]" />}
                      </div>
                      {premium && (
                        <div className="premium-badge">
                          <Lock className="w-3 h-3" />
                          Premium
                        </div>
                      )}
                    </div>
                    <h4 className="text-2xl font-bold text-[#2D3748] mb-2">{app.name}</h4>
                    <p className="text-[#4A5568] leading-relaxed flex-1">{app.description}</p>
                    <button
                      data-testid={`signin-app-${app.id}`}
                      onClick={() => setShowAuth(true)}
                      className="mt-5 inline-flex items-center justify-center gap-2 self-start bg-[#81B29A] text-[#2D3748] hover:bg-[#6A9B84] px-6 py-3 rounded-full font-bold transition-all duration-300 hover:-translate-y-0.5"
                    >
                      {premium ? (
                        <>
                          <Lock className="w-4 h-4" /> Sign in to unlock
                        </>
                      ) : (
                        'Sign in'
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {showAuth && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <button
              data-testid="close-auth-modal"
              onClick={() => setShowAuth(false)}
              className="absolute top-6 right-6 text-[#4A5568] hover:text-[#2D3748] text-2xl font-bold"
            >
              ×
            </button>
            
            <h3 className="text-3xl font-bold text-[#2D3748] mb-6">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <input
                  data-testid="signup-name-input"
                  type="text"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#81B29A]/20 focus:border-[#81B29A] outline-none transition-all"
                  required={!isLogin}
                />
              )}
              
              <input
                data-testid="auth-email-input"
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#81B29A]/20 focus:border-[#81B29A] outline-none transition-all"
                required
              />
              
              <input
                data-testid="auth-password-input"
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#81B29A]/20 focus:border-[#81B29A] outline-none transition-all"
                required
              />
              
              <button
                data-testid="auth-submit-btn"
                type="submit"
                className="w-full bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-6 py-4 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>
            
            <p className="text-center mt-4">
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => {
                  // Carry over whatever they've already typed, so they can just
                  // click through and send the reset email.
                  setForgotEmail(formData.email);
                  setShowAuth(false);
                  setShowForgotPassword(true);
                }}
                className="text-[#81B29A] text-sm font-semibold hover:underline"
              >
                Forgot your password?
              </button>
            </p>
            
            <p className="text-center mt-4 text-[#4A5568]">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#81B29A] font-semibold hover:underline"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setForgotEmail('');
              }}
              className="absolute top-6 right-6 text-[#4A5568] hover:text-[#2D3748] text-2xl font-bold"
            >
              ×
            </button>
            
            <h3 className="text-3xl font-bold text-[#2D3748] mb-2">Reset Password</h3>
            <p className="text-[#4A5568] mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <input
                type="email"
                placeholder="Email address"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#81B29A]/20 focus:border-[#81B29A] outline-none transition-all"
                required
              />
              
              <button
                type="submit"
                disabled={isSendingReset}
                className="w-full bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-6 py-4 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {isSendingReset ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            
            <p className="text-center mt-6 text-[#4A5568]">
              Remember your password?{' '}
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowAuth(true);
                  setIsLogin(true);
                }}
                className="text-[#81B29A] font-semibold hover:underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
